import { type } from 'arktype';

/**
 * Schema for creating a new group.
 */
export const createGroupSchema = type({
  name: type('string').configure({
    description: 'Human-readable group name. Must be unique within the tenant.',
    examples: ['Planning Team', 'Night Shift Leads'],
  }),
  'description?': type('string').configure({
    description: 'Free-text description of the group. Optional.',
    examples: ['Coordinates shift planning across nursing teams'],
  }),
}).configure({ description: 'Payload to create a new user group in the tenant.' });

export type CreateGroup = typeof createGroupSchema.t;

/**
 * Schema for updating an existing group.
 */
export const updateGroupSchema = type({
  'name?': type('string').configure({
    description: 'Updated group name. Optional.',
    examples: ['Planning Team'],
  }),
  'description?': type('string').configure({
    description: 'Updated description. Optional.',
    examples: ['Coordinates shift planning across nursing teams'],
  }),
}).configure({ description: 'Partial update payload for an existing user group.' });

export type UpdateGroup = typeof updateGroupSchema.t;

/**
 * Schema for adding a member to a group.
 */
export const addMemberSchema = type({
  userId: type('string').configure({
    description: 'ID of the user being added to the group.',
    examples: ['usr_abc123'],
  }),
  'isAdmin?': type('boolean').configure({
    description: 'Whether the user joins as a group admin. Defaults to false. Optional.',
    examples: [false, true],
  }),
}).configure({ description: 'Payload to add a user as a member of a group.' });

export type AddMember = typeof addMemberSchema.t;

/**
 * Schema for setting admin status of a group member.
 */
export const setAdminSchema = type({
  isAdmin: type('boolean').configure({
    description: 'New admin flag: true to promote, false to demote.',
    examples: [true, false],
  }),
}).configure({ description: "Payload to promote or demote a group member's admin flag." });

export type SetAdmin = typeof setAdminSchema.t;

/**
 * Schema for assigning a role to a group.
 */
export const assignRoleSchema = type({
  roleId: type('string').configure({
    description: 'ID of the role to assign to every member of the group.',
    examples: ['manager', 'team-lead'],
  }),
}).configure({ description: 'Payload to attach a role to a group (inherited by all members).' });

export type AssignRole = typeof assignRoleSchema.t;

export interface ManagedGroup {
  id: string;
  name: string;
  description: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
  memberCount: number;
  roleCount: number;
}

export interface GroupMember {
  userId: string;
  groupId: string;
  isAdmin: boolean;
  joinedAt: string | Date;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export interface UnassignedUser {
  id: string;
  name: string;
  email: string;
}

export interface GroupRole {
  id: string;
  description: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface GroupPermission {
  id: string;
  description: string | null;
  createdAt: string | Date;
}

export interface GroupDetails {
  group: ManagedGroup;
  members: GroupMember[];
  roles: GroupRole[];
  effectivePermissions: GroupPermission[];
}

/** Direct-array response since the v1 freeze (no `{data}` wrapper). */
export type GroupsResponse = ManagedGroup[];

/** Direct-object response since the v1 freeze (no `{data}` wrapper). */
export type GroupResponse = ManagedGroup;
